import NextLink from "next/link";
import { useRouter as useNextRouter } from "next/compat/router";
import { useParams } from "next/navigation";
import { forwardRef } from "react";
import { locales, defaultLocale } from "./config";

/**
 * Get locale prefix for a given locale
 * Returns empty string for default locale (as-needed prefix strategy)
 */
function getLocalePrefix(locale) {
  return locale && locale !== defaultLocale ? `/${locale}` : "";
}

/**
 * Extract locale from pathname
 */
function getLocaleFromPath(pathname) {
  const segments = pathname?.split("/").filter(Boolean) || [];
  const firstSegment = segments[0];
  return locales?.includes(firstSegment) ? firstSegment : null;
}

/**
 * Locale-aware Link component for Pages Router
 * Automatically prefixes href with the current locale
 */
export const Link = forwardRef(function Link({ href, locale: localeProp, ...props }, ref) {
  const currentLocale = useLocale();
  const targetLocale = localeProp ?? currentLocale;

  // Build the internal href (always includes locale for Next.js routing)
  let internalHref = href;
  // Build the displayed URL (clean URL without locale for default locale)
  let displayedAs = href;

  if (typeof href === "string" && href.startsWith("/")) {
    // Internal href always includes locale prefix for correct routing
    internalHref = `/${targetLocale}${href}`;
    // Display URL: only add prefix for non-default locales
    displayedAs = targetLocale !== defaultLocale ? `/${targetLocale}${href}` : href;
  } else if (typeof href === "object" && href.pathname?.startsWith("/")) {
    internalHref = {
      ...href,
      pathname: `/${targetLocale}${href.pathname}`,
    };
    displayedAs = {
      ...href,
      pathname: targetLocale !== defaultLocale ? `/${targetLocale}${href.pathname}` : href.pathname,
    };
  }

  return (
    <NextLink
      ref={ref}
      href={internalHref}
      as={displayedAs}
      locale={false}
      {...props}
    />
  );
});

/**
 * Locale-aware useRouter hook
 * Returns router with locale-aware push/replace methods
 */
export function useRouter() {
  const router = useNextRouter();
  const currentLocale = useLocale();

  const push = (url, as, options) => {
    const locale = options?.locale ?? currentLocale;

    let internalUrl = url;
    let displayedAs = as;

    if (typeof url === "string" && url.startsWith("/")) {
      internalUrl = `/${locale}${url}`;
      displayedAs = as ?? (locale !== defaultLocale ? `/${locale}${url}` : url);
    } else if (typeof url === "object" && url.pathname?.startsWith("/")) {
      internalUrl = { ...url, pathname: `/${locale}${url.pathname}` };
      displayedAs = as ?? {
        ...url,
        pathname: locale !== defaultLocale ? `/${locale}${url.pathname}` : url.pathname,
      };
    }

    return router?.push(internalUrl, displayedAs, { ...options, locale: false });
  };

  const replace = (url, as, options) => {
    const locale = options?.locale ?? currentLocale;

    let internalUrl = url;
    let displayedAs = as;

    if (typeof url === "string" && url.startsWith("/")) {
      internalUrl = `/${locale}${url}`;
      displayedAs = as ?? (locale !== defaultLocale ? `/${locale}${url}` : url);
    } else if (typeof url === "object" && url.pathname?.startsWith("/")) {
      internalUrl = { ...url, pathname: `/${locale}${url.pathname}` };
      displayedAs = as ?? {
        ...url,
        pathname: locale !== defaultLocale ? `/${locale}${url.pathname}` : url.pathname,
      };
    }

    return router.replace(internalUrl, displayedAs, { ...options, locale: false });
  };

  return {
    ...router,
    locale: currentLocale,
    push,
    replace,
  };
}

/**
 * Get current locale from router
 * Returns the current locale, falling back to defaultLocale
 */
export function useLocale() {
  const router = useNextRouter();
  const params = useParams();

  return router?.locale || getLocaleFromPath(router?.asPath) || params?.locale || defaultLocale;
}

/**
 * Locale-aware usePathname hook
 * Returns the pathname without the locale prefix
 */
export function usePathname() {
  const router = useNextRouter();
  const pathname = router?.asPath?.split("?")[0] || "/";

  // Remove locale prefix if present
  for (const locale of locales || []) {
    if (pathname.startsWith(`/${locale}/`)) {
      return pathname.slice(locale.length + 1);
    }
    if (pathname === `/${locale}`) {
      return "/";
    }
  }

  return pathname;
}

/**
 * Server-side redirect with locale support
 */
export function redirect(url, locale) {
  const prefix = getLocalePrefix(locale || defaultLocale);
  const localizedUrl = url.startsWith("/") ? `${prefix}${url}` : url;

  return {
    redirect: {
      destination: localizedUrl,
      permanent: false,
    },
  };
}

export function getDelocalizedPath(pathname) {
  const currentLocale = getLocaleFromPath(pathname);

  if (pathname.startsWith(`/${currentLocale}/`)) {
    return pathname.slice(currentLocale.length + 1);
  }

  return pathname;
}
